import { useCallback, useEffect, useRef, useState } from 'react';

import { useApi } from '../lib/api-context';
import type { ApiClient, SessionMeta } from '../lib/api-client';

const SESSIONS_CHANGED_EVENT = 'bluebird:sessions:changed';

/** Dispatch the cross-component "refetch sessions" signal. */
export function notifySessionsChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(SESSIONS_CHANGED_EVENT));
}

interface UseSessionListResult {
  sessions: SessionMeta[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Server-backed list of every session this user owns. Re-fetches on:
 *   - first mount,
 *   - `visibilitychange` → 'visible' (user came back to the tab),
 *   - the `bluebird:sessions:changed` window event (dispatched by the
 *     WS listener when the server pushes a `session.title.updated`,
 *     and by `LeftSidebar` after a successful delete RPC).
 *
 * The hook is intentionally minimal — no caching, no polling. Clients
 * that want optimistic updates merge server data with their own local
 * tab list (chat + file tabs) themselves.
 */
export function useSessionList(): UseSessionListResult {
  const api = useApi();
  const [sessions, setSessions] = useState<SessionMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOnceRef = useRef<(client: ApiClient) => Promise<void>>(
    async () => {},
  );
  fetchOnceRef.current = useCallback(
    async (client: ApiClient): Promise<void> => {
      setLoading(true);
      try {
        const list = await client.listSessions();
        setSessions(list);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const refresh = useCallback(() => {
    void fetchOnceRef.current(api);
  }, [api]);

  useEffect(() => {
    void fetchOnceRef.current(api);
  }, [api]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        void fetchOnceRef.current(api);
      }
    };
    const onChange = () => {
      void fetchOnceRef.current(api);
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(SESSIONS_CHANGED_EVENT, onChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(SESSIONS_CHANGED_EVENT, onChange);
    };
  }, [api]);

  return { sessions, loading, error, refresh };
}
